import { Box } from '@hivebrite/design-system/Box';
import { Divider } from '@hivebrite/design-system/Divider';

import ImageArrangement from 'hb-builder/components/BuilderComponents/block/Hero/Settings/Content/BackgroundImage/ImageArrangement';
import Button from 'hb-builder/components/BuilderComponents/block/Hero/Settings/Content/Button';
import { sectionImageRecommendedSize } from 'hb-builder/components/BuilderComponents/constants/imageSizes';
import DeleteItemModal from 'hb-builder/components/common/modal/DeleteItemModal';
import ColorSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/ColorSetting';
import DeleteImageSource from 'hb-builder/components/common/SettingsLibrary/CommonSettings/DeleteImageSource';
import DisplayOption from 'hb-builder/components/common/SettingsLibrary/CommonSettings/DisplayOption';
import EditImageSource from 'hb-builder/components/common/SettingsLibrary/CommonSettings/EditImageSource';
import TextSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/TextSetting';
import InputTitle from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/InputTitle';
import SettingBlock from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/SettingBlock';
import { useBuilderContext, useBuilderConfigContext } from 'hb-builder/hooks';
import type {
  ComponentSettingsTab,
  OnChangeSettingsArgs,
} from 'hb-builder/types/settings';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { ButtonItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/buttonItemAttributes';
import type { HeroItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/heroItemAttributes';
import type { SectionItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/sectionItemAttributes';

interface Slide {
  attributes: SectionItemAttributes;
  children: [
    DocumentItem<HeroItemAttributes>,
    DocumentItem<HeroItemAttributes>,
    DocumentItem<ButtonItemAttributes>,
  ];
  id: string;
  type: string;
}

type SlideContentProps = {
  componentName: string;
  oneItemOnly: boolean;
  removeItemOpen: (id: string) => void;
  item: Slide;
} & Pick<ComponentSettingsTab<SectionItemAttributes>, 'onChange'>;

const SlideContent = ({
  componentName,
  onChange,
  oneItemOnly,
  removeItemOpen,
  item: slide,
}: SlideContentProps) => {
  const { getParentItem, updateAttribute } = useBuilderContext();
  const {
    features: { color },
    hivebrite: { networkColors },
  } = useBuilderConfigContext();
  const colorSettingsProps = {
    color,
    networkColors,
  };
  const [headline, subline, button] = slide.children;
  const heroSlider = getParentItem(slide.id);

  const handleChange = (args: OnChangeSettingsArgs) => {
    updateAttribute({
      id: heroSlider?.id,
      attribute: '__lastItemModified',
      value: slide.id,
    });

    onChange({ ...args });
  };

  const hasImage = Boolean(slide.attributes.containerBackgroundImage);
  const settingProps = { item: slide };

  return (
    <>
      <SettingBlock boxProps={{ mt: 1 }} title={__('Text')}>
        <DisplayOption
          item={headline}
          onChange={handleChange}
          title={__('Display headline')}
        />
        <TextSetting
          item={headline}
          onChange={handleChange}
          placeholder={__('Enter a headline')}
          settingKey="content"
          width="100%"
        />
        <DisplayOption
          item={subline}
          onChange={handleChange}
          title={__('Display subline')}
        />
        <TextSetting
          item={subline}
          onChange={handleChange}
          placeholder={__('Enter a subline')}
          settingKey="content"
          width="100%"
        />
      </SettingBlock>
      <Divider m={0} />
      <SettingBlock boxProps={{ mt: 4 }} title={__('Button')}>
        <DisplayOption
          title={__('Display button')}
          item={button}
          onChange={handleChange}
        />
        <Button item={button} onChange={handleChange} />
      </SettingBlock>
      <Divider m={0} />
      <SettingBlock boxProps={{ mt: 4 }} title={__('Background Image')}>
        <InputTitle
          boxProps={{ mb: 3 }}
          id={`containerBackgroundColor-bg-${slide.id}`}
          label={__('Background')}
        />
        <ColorSetting
          componentHasMultiplePickers
          onChange={handleChange}
          settingKey="containerBackgroundColor"
          title={__('Color background')}
          transparent
          {...settingProps}
          {...colorSettingsProps}
        />
        <InputTitle
          boxProps={{ mb: 3 }}
          id={`containerBackgroundColor-bg-image-${slide.id}`}
          label={__('Image background')}
        />
        <Box my={2}>
          {s__(
            'Builder|Recommended %{size}px. The file must not exceed 2.5 MB',
          ).i__({
            size: sectionImageRecommendedSize,
          })}
        </Box>
        <EditImageSource hasImage={hasImage} id={slide.id} />
        {hasImage && (
          <DeleteImageSource
            onChange={handleChange}
            settingKey="attributes.containerBackgroundImage"
            {...settingProps}
          />
        )}
        <ImageArrangement onChange={handleChange} {...settingProps} />
        <TextSetting
          description={s__(
            "Builder|This will appear if the image can't be displayed",
          )}
          onChange={handleChange}
          settingKey="attributes.containerBackgroundImageAlt"
          title={__('Alternative text')}
          {...settingProps}
        />
      </SettingBlock>
      <SettingBlock boxProps={{ mt: 1, pb: 5 }} title="">
        <DeleteItemModal
          id={slide.id}
          itemName={componentName}
          isDisabled={oneItemOnly}
          onConfirm={() => removeItemOpen(slide.id)}
        />
      </SettingBlock>
    </>
  );
};

export default SlideContent;
